import React from 'react'
import {useLocation} from 'react-router-dom'
import { initializeQuoraTracking, trackRoutePageView } from './utils/tracking';

const params = [
  'isAuth',
  'username',
  'paid',
  'advanced',
  'stocks',
  'email',
  'preferences',
  'accountSessionManagementAvailable',
];
const defaultValues = {
  isAuth: undefined,
  username: undefined,
  paid: undefined,
  advanced: undefined,
  stocks: null,
  email: undefined,
  preferences: undefined,
  accountSessionManagementAvailable: false,
};

const mergeResponseIntoStore = (currentStore, response) => {
  if (!response) {
    return currentStore;
  }

  const prepare = {};
  for (const name of params) {
    if (![null, undefined].includes(response[name])) {
      if (JSON.stringify(currentStore[name]) !== JSON.stringify(response[name])) {
        prepare[name] = response[name];
      }
    }
  }

  if (Object.keys(prepare).length === 0) {
    return currentStore;
  }
  if (prepare.isAuth === false) {
    Object.assign(prepare, defaultValues, {isAuth: false});
  }
  return {...currentStore, ...prepare};
};

const StoreContext = React.createContext({});

const Store = ({children}) => {
  const location = useLocation();
  const [store, setStore] = React.useState(defaultValues);
  const lastTrackedRouteRef = React.useRef('');

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search, store.username])

  React.useEffect(() => {
    initializeQuoraTracking(store.email);
  }, [store.email]);

  React.useEffect(() => {
    const routePath = `${location.pathname}${location.search}${location.hash || ''}`;
    if (!lastTrackedRouteRef.current) {
      // Initial page-load is already tracked by base pixels in index.html.
      lastTrackedRouteRef.current = routePath;
      return;
    }
    if (routePath === lastTrackedRouteRef.current) {
      return;
    }
    lastTrackedRouteRef.current = routePath;
    trackRoutePageView(routePath, store.email);
  }, [location.pathname, location.search, location.hash, store.email]);

  const setResponse = React.useCallback((response) => {
    setStore((currentStore) => mergeResponseIntoStore(currentStore, response));
  }, []);

  const value = React.useMemo(() => ({
    store,
    setResponse,
  }), [store, setResponse]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
};

export {Store, StoreContext, defaultValues, mergeResponseIntoStore};
